import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api, ApiError } from '../api/client';
import type { AuditRow, UpstreamCallRow } from '../api/types';
import { Badge } from '../components/Badge';
import { Card, PageHeader } from '../components/Card';
import { JsonView } from '../components/JsonView';
import { EmptyState, ErrorBanner, LoadingBlock } from '../components/Feedback';
import { Button, Field, Input, Select } from '../components/Form';
import { Pagination } from '../components/Pagination';
import { DataTable, Mono } from '../components/Table';
import { ago, dateTime, millis, text } from '../lib/format';

type Tab = 'upstream' | 'audit';

export function LogsPage() {
  const [tab, setTab] = useState<Tab>('upstream');

  return (
    <div className="space-y-4">
      <PageHeader title="Логи" description="Журналы вызовов upstream и аудит-лог" />

      <div className="flex gap-2">
        <Button size="sm" variant={tab === 'upstream' ? 'primary' : 'ghost'} onClick={() => setTab('upstream')}>
          Upstream вызовы
        </Button>
        <Button size="sm" variant={tab === 'audit' ? 'primary' : 'ghost'} onClick={() => setTab('audit')}>
          Аудит
        </Button>
      </div>

      {tab === 'upstream' ? <UpstreamTab /> : <AuditTab />}
    </div>
  );
}

function UpstreamTab() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [route, setRoute] = useState('');
  const [routeInput, setRouteInput] = useState('');
  const [ok, setOk] = useState('');
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  const path = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (route) params.set('route', route);
    if (ok) params.set('ok', ok);
    return `/admin-api/logs/upstream?${params.toString()}`;
  }, [page, limit, route, ok]);

  const query = useQuery<{ list: UpstreamCallRow[]; total: number }, ApiError>({
    queryKey: ['api', path],
    queryFn: ({ signal }) => api.get(path, signal),
    placeholderData: (prev) => prev,
    gcTime: 0,
    staleTime: 0,
    retry: 1,
  });

  const list = query.data?.list ?? [];
  const total = query.data?.total ?? 0;

  return (
    <div className="space-y-4">
      <Card title="Фильтры" bodyClassName="space-y-3">
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <Field label="Route">
            <Input value={routeInput} onChange={(e) => setRouteInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { setRoute(e.currentTarget.value.trim()); setPage(1); } }} placeholder="/api/record" />
          </Field>
          <Field label="Результат">
            <Select value={ok} onChange={(e) => { setOk(e.target.value); setPage(1); }}>
              <option value="">Любой</option>
              <option value="1">OK</option>
              <option value="0">Ошибка</option>
            </Select>
          </Field>
          <div className="flex items-end gap-2">
            <Button variant="primary" onClick={() => { setRoute(routeInput.trim()); setPage(1); }}>Найти</Button>
            <Button variant="ghost" onClick={() => { setRoute(''); setRouteInput(''); setOk(''); setPage(1); }}>Сбросить</Button>
          </div>
        </div>
      </Card>

      <Card noPadding>
        {query.error ? (
          <div className="p-3"><ErrorBanner message={query.error.message} onRetry={() => query.refetch()} /></div>
        ) : query.isLoading && !list.length ? (
          <LoadingBlock />
        ) : list.length === 0 ? (
          <EmptyState title="Записей нет" />
        ) : (
          <>
            <DataTable<UpstreamCallRow>
              columns={[
                { key: 'id', header: 'ID', width: '4rem', hide: 'sm', render: (r) => <Mono>{r.id}</Mono> },
                { key: 'route', header: 'Route', render: (r) => <Mono>{r.route}</Mono> },
                { key: 'method', header: 'Method', hide: 'md', width: '4rem', render: (r) => <Mono>{r.method}</Mono> },
                { key: 'http_status', header: 'HTTP', align: 'right', width: '4rem', render: (r) => <span className={r.http_status && r.http_status >= 400 ? 'text-red-400' : ''}>{r.http_status ?? '—'}</span> },
                { key: 'ok', header: 'OK', width: '4rem', align: 'center', render: (r) => <Badge tone={r.ok ? 'success' : 'danger'}>{r.ok ? 'OK' : 'ERR'}</Badge> },
                { key: 'msg', header: 'Сообщение', hide: 'lg', render: (r) => <span className="truncate">{text(r.msg)}</span> },
                { key: 'duration_ms', header: 'Длительность', align: 'right', width: '5rem', render: (r) => millis(r.duration_ms) },
                { key: 'created', header: 'Время', align: 'right', width: '7rem', render: (r) => ago(r.created_at) },
              ]}
              rows={list}
              rowKey={(r) => r.id}
              onRowClick={(r) => setExpandedRow(expandedRow === r.id ? null : r.id)}
              loading={query.isFetching}
              rowClassName={(r) => expandedRow === r.id ? 'bg-zinc-800/40' : undefined}
              minWidth="36rem"
            />
            {expandedRow !== null ? (
              <div className="border-t border-zinc-800 p-3 space-y-2">
                {(() => {
                  const row = list.find((r) => r.id === expandedRow);
                  if (!row) return null;
                  return (
                    <>
                      <div className="grid gap-2 sm:grid-cols-3 text-xs">
                        <div><span className="text-zinc-500">API code: </span><Mono>{row.api_code ?? '—'}</Mono></div>
                        <div><span className="text-zinc-500">Error: </span><span className="text-red-400">{text(row.error)}</span></div>
                        <div><span className="text-zinc-500">Создан: </span>{dateTime(row.created_at)}</div>
                      </div>
                      <JsonView value={row.request} label="Запрос" />
                      <JsonView value={row.response} label="Ответ" />
                    </>
                  );
                })()}
              </div>
            ) : null}
            <div className="border-t border-zinc-800 px-3 py-2 sm:px-4">
              <Pagination page={page} limit={limit} total={total} count={list.length} onPage={setPage} onLimit={(v) => { setLimit(v); setPage(1); }} />
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

function AuditTab() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  const path = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    return `/admin-api/audit?${params.toString()}`;
  }, [page, limit]);

  const query = useQuery<{ list: AuditRow[] }, ApiError>({
    queryKey: ['api', path],
    queryFn: ({ signal }) => api.get(path, signal),
    placeholderData: (prev) => prev,
    gcTime: 0,
    staleTime: 0,
    retry: 1,
  });

  const list = query.data?.list ?? [];

  return (
    <Card noPadding>
      {query.error ? (
        <div className="p-3"><ErrorBanner message={query.error.message} onRetry={() => query.refetch()} /></div>
      ) : query.isLoading && !list.length ? (
        <LoadingBlock />
      ) : list.length === 0 ? (
        <EmptyState title="Записей аудита нет" />
      ) : (
        <>
          <DataTable<AuditRow>
            columns={[
              { key: 'id', header: 'ID', width: '4rem', hide: 'sm', render: (r) => <Mono>{r.id}</Mono> },
              { key: 'actor', header: 'Кто', width: '6rem', render: (r) => <span className="truncate">{r.actor}</span> },
              { key: 'action', header: 'Действие', render: (r) => <Badge tone="info">{r.action}</Badge> },
              { key: 'target', header: 'Объект', hide: 'md', render: (r) => <Mono>{r.target ?? '—'}</Mono> },
              { key: 'created', header: 'Время', align: 'right', width: '8rem', render: (r) => dateTime(r.created_at) },
            ]}
            rows={list}
            rowKey={(r) => r.id}
            loading={query.isFetching}
            minWidth="28rem"
          />
          <div className="border-t border-zinc-800 px-3 py-2 sm:px-4">
            <Pagination page={page} limit={limit} count={list.length} onPage={setPage} onLimit={(v) => { setLimit(v); setPage(1); }} />
          </div>
        </>
      )}
    </Card>
  );
}
